/**
 * The transport shared by the web app and the mobile app.
 *
 * Deliberately free of React and TanStack Query - those stay in the web hooks
 * layer, because the mobile app will want different caching and the client
 * should not care. Everything platform-specific is injected: the base URL, how
 * to get a token, and which `fetch` to use.
 *
 * Every Netlify function replies with a `{ data }` envelope (plus `meta` on
 * paginated routes) and `{ error }` on failure, so unwrapping lives here rather
 * than being repeated at ~90 call sites.
 */
import type { PaginatedResult, PaginationMeta } from './api.ts';
import { ApiError, kindForStatus } from './apiError.ts';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  /**
   * Prefix for every request path. The web passes '/api' and relies on
   * same-origin cookies; mobile passes an absolute origin that varies per
   * environment. A trailing slash is stripped.
   */
  baseUrl: string;
  /**
   * Supplies a bearer token. Omit on web, where the Clerk session cookie is
   * sent automatically and an Authorization header would be redundant.
   *
   * Returning null sends the request unauthenticated. That is correct during a
   * cold start where Clerk has not yet restored the session: the server answers
   * 401 and the caller sees `kind: 'unauthorized'` rather than a hang.
   */
  getToken?: () => Promise<string | null> | string | null;
  /** Defaults to global fetch. React Native's differs, notably for multipart. */
  fetch?: FetchLike;
  /** Milliseconds before a request is aborted. Omit or 0 to disable. */
  timeoutMs?: number;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialised as JSON. Use `upload` for multipart. */
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Fallback message when the server's error response carries no `error`
   * string. Defaults to 'Request failed'. Set it where the wording is product
   * copy the user actually reads - the upload paths surface it directly.
   */
  errorMessage?: string;
}

/** Options for the verb helpers, which take the body as a positional argument. */
export type BodylessOptions = Omit<RequestOptions, 'method' | 'body'>;

export interface ApiClient {
  /** Issue a request and unwrap `json.data`. */
  request: <T>(path: string, options?: RequestOptions) => Promise<T>;
  get: <T>(path: string, options?: BodylessOptions) => Promise<T>;
  post: <T>(path: string, body?: unknown, options?: BodylessOptions) => Promise<T>;
  patch: <T>(path: string, body?: unknown, options?: BodylessOptions) => Promise<T>;
  put: <T>(path: string, body?: unknown, options?: BodylessOptions) => Promise<T>;
  del: <T>(path: string, body?: unknown, options?: BodylessOptions) => Promise<T>;
  /** For routes returning `{ data, meta }`; keeps the pagination envelope. */
  getPage: <T>(path: string, options?: BodylessOptions) => Promise<PaginatedResult<T>>;
  /**
   * Multipart upload. Content-Type is deliberately left unset so the platform
   * writes its own multipart boundary - setting it by hand produces a request
   * the server cannot parse.
   */
  upload: <T>(path: string, form: FormData, options?: BodylessOptions) => Promise<T>;
  /** The resolved base URL, for logging and diagnostics. */
  readonly baseUrl: string;
}

interface Envelope {
  data?: unknown;
  meta?: unknown;
  error?: unknown;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  const doFetch: FetchLike = options.fetch ?? ((input, init) => fetch(input, init));
  const { getToken, timeoutMs } = options;

  const urlFor = (path: string) => `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;

  async function authHeaders(): Promise<Record<string, string>> {
    if (!getToken) return {};
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Shared request path. `serialise` decides how the body becomes a fetch body
   * and which headers come with it, which is the only real difference between
   * a JSON call and a multipart one.
   */
  async function send(
    path: string,
    init: RequestInit,
    extraHeaders: Record<string, string>,
    signal?: AbortSignal,
    errorMessage = 'Request failed'
  ): Promise<{ envelope: Envelope; url: string }> {
    const url = urlFor(path);

    // Compose the caller's signal with our timeout so either can abort.
    const controller = timeoutMs ? new AbortController() : null;
    const timer = controller && timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
    if (controller && signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    let res: Response;
    try {
      res = await doFetch(url, {
        ...init,
        headers: { ...extraHeaders, ...(await authHeaders()), ...(init.headers ?? {}) },
        signal: controller?.signal ?? signal,
      });
    } catch (cause) {
      // fetch rejects only for network-level failures and aborts; every HTTP
      // status, including 5xx, resolves normally.
      const aborted = (isRecord(cause) && cause.name === 'AbortError') || signal?.aborted === true;
      throw new ApiError({
        kind: aborted ? 'aborted' : 'offline',
        message: aborted ? 'Request cancelled' : 'Network request failed',
        url,
        cause,
      });
    } finally {
      if (timer) clearTimeout(timer);
    }

    // Read via res.json() rather than res.text() + JSON.parse. Both work in a
    // real runtime, but the suite's hand-rolled Response mocks implement only
    // { ok, status, json }, and the tests around these calls are the regression
    // net for the whole migration - it is not worth rewriting 17 of them to
    // gain a raw-body string in one error path.
    let envelope: Envelope = {};
    let parsed = true;
    try {
      envelope = ((await res.json()) as Envelope | null) ?? {};
    } catch {
      parsed = false;
    }

    // An unparseable success is a real failure: the caller is about to read
    // `data` off nothing. A 204 legitimately has no body.
    if (res.ok && !parsed && res.status !== 204) {
      throw new ApiError({
        kind: 'malformed',
        message: errorMessage,
        status: res.status,
        url,
      });
    }

    if (!res.ok) {
      // Preserve the exact message the old per-file helpers produced, so the
      // strings rendered in the web UI are unchanged.
      const message =
        typeof envelope.error === 'string' && envelope.error ? envelope.error : errorMessage;
      throw new ApiError({
        kind: kindForStatus(res.status),
        message,
        status: res.status,
        url,
        body: envelope,
      });
    }

    return { envelope, url };
  }

  const jsonInit = (method: string, body: unknown): RequestInit => ({
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    const { envelope } = await send(
      path,
      jsonInit(opts.method ?? 'GET', opts.body),
      { 'Content-Type': 'application/json', ...(opts.headers ?? {}) },
      opts.signal,
      opts.errorMessage
    );
    return envelope.data as T;
  }

  const withBody =
    (method: RequestOptions['method']) =>
    <T>(path: string, body?: unknown, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
      request<T>(path, { ...opts, method, body });

  return {
    baseUrl,
    request,
    get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
      request<T>(path, { ...opts, method: 'GET' }),
    post: withBody('POST'),
    patch: withBody('PATCH'),
    put: withBody('PUT'),
    del: withBody('DELETE'),

    async getPage<T>(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}) {
      const { envelope } = await send(
        path,
        { method: 'GET' },
        { 'Content-Type': 'application/json', ...(opts.headers ?? {}) },
        opts.signal,
        opts.errorMessage
      );
      return {
        data: (envelope.data ?? []) as T[],
        meta: envelope.meta as PaginationMeta,
      } satisfies PaginatedResult<T>;
    },

    async upload<T>(
      path: string,
      form: FormData,
      opts: Omit<RequestOptions, 'method' | 'body'> = {}
    ) {
      const { envelope } = await send(
        path,
        { method: 'POST', body: form },
        { ...(opts.headers ?? {}) },
        opts.signal,
        opts.errorMessage
      );
      return envelope.data as T;
    },
  };
}
